import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { moveDealFolder } from '../../../../api/mutations';
import { getDealFolders } from '../../../../api/queries_v2';
import { FolderTreeSelector } from '../../../../components/folder-tree-selector';
import PropdocsModal from '../../../../components/modal/PropdocsModal';
import { useNotifications } from '../../../../notifications';

interface MoveDealFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  teamId: string;
  onSuccess: () => void;
}

const MoveDealFolderModal: React.FC<MoveDealFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  teamId,
  onSuccess,
}) => {
  const [selectedParentFolderId, setSelectedParentFolderId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Fetch all deal folders for the tree selector
  const { data: foldersData, loading: foldersLoading } = useQuery(getDealFolders as any, {
    variables: { teamId },
    skip: !isOpen,
  });

  const [moveFolder] = useMutation(moveDealFolder, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder moved successfully' });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      console.error('Error moving folder:', error);
      setIsSubmitting(false);
      notifyError({ message: error.message || 'Failed to move folder. Please try again.' });
    },
  });

  const handleClose = () => {
    setSelectedParentFolderId(null);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await moveFolder({
        variables: {
          folderId,
          parentFolderId: selectedParentFolderId,
        },
        refetchQueries: [
          {
            query: getDealFolders,
            variables: { teamId },
          },
        ],
      });
    } catch (error) {
      // Error handling is done in the onError callback
      console.error('Move folder error:', error);
    }
  };

  // Filter out the current folder and its children from the available parent folders
  const availableFolders =
    foldersData?.dealFolders?.folders?.filter((folder: any) => {
      // Can't move to itself
      if (folder.id === folderId) return false;

      // Can't move to its own children (would create circular reference)
      const isChild = (parentId: string): boolean => {
        const parent = foldersData?.dealFolders?.folders?.find((f: any) => f.id === parentId);
        if (!parent) return false;
        if (parent.parentFolder?.id === folderId) return true;
        if (parent.parentFolder?.id) return isChild(parent.parentFolder.id);
        return false;
      };

      return !isChild(folder.id);
    }) || [];

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isSubmitting}
      title={<FormattedMessage id="deals.folders.move" defaultMessage="Move Folder" />}
    >
      <>
        <div>
          <div className="mb-3">
            <p>Move '{folderName}' to a different location:</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">
                <FormattedMessage id="deals.folders.selectParentFolder" defaultMessage="Select Parent Folder" />
              </label>
              {foldersLoading ? (
                <div className="text-muted small">
                  <FormattedMessage id="deals.folders.loading" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={availableFolders}
                  selectedFolderId={selectedParentFolderId}
                  onSelectionChange={setSelectedParentFolderId}
                  disabled={isSubmitting}
                  context="deals"
                />
              )}
            </div>
          </form>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="common.moving" defaultMessage="Moving..." />
            ) : (
              <FormattedMessage id="deals.folders.move" defaultMessage="Move Folder" />
            )}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default MoveDealFolderModal;
